/**
 * Customize to change styles
 */

import { PasswordInputSx } from '@/components/blocks/PasswordInput/styles/style';
import { useLocalization } from '@/data/Localization';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import {
	FormControl,
	FormHelperText,
	IconButton,
	InputAdornment,
	InputLabel,
	OutlinedInput,
	SxProps,
	Theme,
} from '@mui/material';
import { FC, useId, useState } from 'react';

type Props = {
	label: string;
	name: string;
	value?: string;
	disabled?: boolean;
	placeholder?: string;
	inputProps?: object;
	required?: boolean;
	sx?: SxProps<Theme>;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	error?: boolean;
	autoFocus?: boolean;
	helperText?: string;
	onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
};

export const PasswordInput: FC<Props> = ({
	label,
	name,
	value,
	disabled,
	placeholder,
	inputProps,
	required,
	sx,
	onChange,
	error = false,
	autoFocus = false,
	helperText = '',
	onBlur,
}) => {
	const id = useId();
	const [showPassword, setShowPassword] = useState<boolean>(true);
	const passwordInputNLS = useLocalization('PasswordInput');

	return (
		<FormControl variant="outlined" fullWidth error={error} required={required} sx={sx}>
			<InputLabel htmlFor={`${id}-adorned-password`}>{label}</InputLabel>
			<OutlinedInput
				id={`${id}-adorned-password`}
				data-testid={`${id}-adorned-password`}
				name={name}
				type={showPassword ? 'password' : 'text'}
				value={value}
				autoFocus={autoFocus}
				onChange={onChange}
				inputProps={inputProps}
				disabled={disabled}
				autoComplete="off"
				placeholder={value === '' ? placeholder : ''}
				error={error ?? null}
				onBlur={onBlur ?? onBlur}
				endAdornment={
					<InputAdornment position="end">
						<IconButton
							aria-label={passwordInputNLS.toggleLabel.t()}
							onClick={() => setShowPassword(!showPassword)}
							onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) => {
								event.preventDefault();
							}}
							edge="end"
							disableRipple
							disableTouchRipple
							disableFocusRipple
							sx={PasswordInputSx.iconButton}
						>
							{showPassword ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
						</IconButton>
					</InputAdornment>
				}
				label={label}
			/>
			{helperText ? (
				<FormHelperText sx={PasswordInputSx.helperText}>{helperText}</FormHelperText>
			) : null}
		</FormControl>
	);
};
